"use client";

import { useEffect } from "react";
import { useSession } from "next-auth/react";
import { dispatchDealerOrdersInvalidated } from "@/lib/orders/client-events";
import { ACCOUNT_SESSION_ENDED_EVENT, isAccountSessionEndReason } from "@/lib/account-session-events";
import {
  ORDER_DATA_INVALIDATED_EVENT,
  ORDER_EVENT_TOPICS,
  parseOrderEventTopics,
  type OrderEventTopic,
} from "@/lib/order-events/client";

/** One stream per signed-in tab, shared by pages, conversations and badges. */
export function OrderEventsProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const sessionId = session?.accountSession?.id;
  const user = session?.user;
  const identity = status === "authenticated" && user?.isActive
    ? [user.id, user.role, user.companyId, user.companyRole, user.companyStatus, session?.accountSession?.id].join(":")
    : null;

  useEffect(() => {
    if (!identity) return;
    let source: EventSource | null = null;
    let connected = false;
    const publish = (topics: readonly OrderEventTopic[]) => {
      if (!topics.length) return;
      window.dispatchEvent(new CustomEvent(ORDER_DATA_INVALIDATED_EVENT, { detail: { topics } }));
      if (topics.includes("orders")) dispatchDealerOrdersInvalidated(window);
      if (topics.includes("notifications")) {
        window.dispatchEvent(new Event("cfmoto:notifications-updated"));
      }
    };
    const resync = () => publish(ORDER_EVENT_TOPICS);
    const connect = () => {
      if (typeof EventSource === "undefined") return;
      source?.close();
      source = new EventSource("/api/order-events");
      source.addEventListener("ready", () => {
        connected = true;
        // NOTIFY is ephemeral: a fresh snapshot covers reconnect gaps.
        resync();
      });
      source.addEventListener("invalidate", (event) => {
        publish(parseOrderEventTopics((event as MessageEvent<string>).data));
      });
      source.addEventListener("session-ended", (event) => {
        source?.close();
        connected = false;
        let reason = "session-ended";
        try {
          const value = JSON.parse((event as MessageEvent<string>).data)?.reason;
          if (isAccountSessionEndReason(value)) reason = value;
        } catch { /* Older servers send an empty termination envelope. */ }
        window.dispatchEvent(new CustomEvent(ACCOUNT_SESSION_ENDED_EVENT, { detail: { sessionId, reason } }));
      });
      source.onerror = () => { connected = false; };
    };
    connect();
    const fallback = window.setInterval(() => {
      if (connected || document.visibilityState === "hidden") return;
      resync();
      // Native EventSource normally retries itself. HTTP authorization errors
      // can close it permanently; a later session refresh may restore access.
      if (!source || source.readyState === EventSource.CLOSED) connect();
    }, 30_000);
    window.addEventListener("focus", resync);
    window.addEventListener("online", resync);
    return () => {
      source?.close();
      window.clearInterval(fallback);
      window.removeEventListener("focus", resync);
      window.removeEventListener("online", resync);
    };
  }, [identity, sessionId]);

  return children;
}
